import { useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { Anthem } from '@/model';
import { useDeleteAnthemFileMutation, usePutAnthemMutation } from '@/services/queries/anthem';
import { TextareaField } from '@/components/TextareaField';
import { AudioPreview } from '@/components/AudioPreview';
import { ModalFormData, modalFormSchema } from './schemas';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface AnthemEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  item: Anthem;
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const AnthemEditModal = ({ item, isOpen, onClose, onDone }: AnthemEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutAnthemMutation();
  const deleteMutation = useDeleteAnthemFileMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
    setValue,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      location: item.location,
      file: '',
      lyrics: item.lyrics,
    },
    resolver: zodResolver(modalFormSchema),
    mode: 'onChange',
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onHandleDelete = (item: Anthem) => {
    deleteMutation.mutate(item.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_history_anthem.application_history_anthem_delete_success'),
          status: 'success',
        });
        handleClose();
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_history_anthem.application_history_anthem_delete_error'),
        });
      },
    });
  };

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        file: data?.file,
        lyrics: data?.lyrics ?? '',
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            reset();
            toast({
              status: 'success',
              title: t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_error'),
            });
          },
        },
      );
    },
    [onDone, putItemMutation, item, reset, toast, t],
  );

  useEffect(() => {
    if (isOpen && item) {
      reset({
        team_uuid: item.team_uuid ?? undefined,
        location: item.location,
        file: undefined,
        lyrics: item.lyrics,
      });
    }
  }, [isOpen, item, reset]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={e => handleSubmit(onSubmit)(e)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_team')}
              placeholder={t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />
            <Text fontWeight='medium'>{t('pages.application_history_anthem.anthem_edit_modal.anthem_label_lyrics')}</Text>
            <TextareaField
              rows={6}
              maxLength={2000}
              helperText={t('pages.application_history_anthem.anthem_edit_modal.anthem_helper_text')}
              error={errors?.lyrics?.message}
              {...register('lyrics')}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <AudioPreview
                fileUrl={item.location ?? ''}
                onFileChange={file => {
                  setValue('file', file);
                }}
                onDelete={() => onHandleDelete(item)}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_history_anthem.anthem_edit_modal.anthem_edit_save')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
